import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "../api/client";
import TopBar from "../components/layout/TopBar";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Archive, Trash2, Search, ExternalLink } from "lucide-react";
import { Input } from "../components/ui/input";
import { format } from "date-fns";
import { toast } from "sonner";

const PRIORITY_COLORS = {
  Low: "bg-gray-50 text-gray-600 border-gray-200",
  Normal: "bg-blue-50 text-blue-600 border-blue-200",
  High: "bg-orange-50 text-orange-600 border-orange-200",
  Urgent: "bg-red-50 text-red-600 border-red-200",
};

export default function ArchivePage() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [search, setSearch] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = () => API.get("/tasks-archived").then(r => setTasks(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleDelete = async (taskId) => {
    try {
      await API.delete(`/tasks/${taskId}`);
      toast.success("Task permanently deleted");
      setDeleteConfirm(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const filtered = search
    ? tasks.filter(t => t.title.toLowerCase().includes(search.toLowerCase()) || t.task_number.toLowerCase().includes(search.toLowerCase()))
    : tasks;

  return (
    <>
      <TopBar title="Archive" />
      <div className="flex-1 overflow-y-auto p-6" data-testid="archive-page">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Archive className="w-5 h-5 text-[#686B74]" />
            <p className="text-sm text-[#686B74]">{tasks.length} archived task{tasks.length !== 1 ? "s" : ""}</p>
          </div>
          <div className="relative w-64">
            <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-[#686B74]" />
            <Input
              data-testid="archive-search"
              placeholder="Search archived tasks..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
        </div>

        <div className="bg-white border border-[#EBEBEB] rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#EBEBEB] bg-[#F9F9FB]">
                  <th className="text-left px-4 py-2 font-medium text-[#686B74] text-xs">ID</th>
                  <th className="text-left px-4 py-2 font-medium text-[#686B74] text-xs">Title</th>
                  <th className="text-left px-4 py-2 font-medium text-[#686B74] text-xs">Creator</th>
                  <th className="text-left px-4 py-2 font-medium text-[#686B74] text-xs">Owner</th>
                  <th className="text-left px-4 py-2 font-medium text-[#686B74] text-xs">Priority</th>
                  <th className="text-left px-4 py-2 font-medium text-[#686B74] text-xs">Archived</th>
                  <th className="text-right px-4 py-2 font-medium text-[#686B74] text-xs">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t, i) => (
                  <tr
                    key={t.id}
                    data-testid={`archive-row-${i}`}
                    className="border-b border-[#EBEBEB] last:border-0 hover:bg-[#F9F9FB] transition-colors"
                  >
                    <td className="px-4 py-2.5 text-xs text-[#686B74] font-mono">{t.task_number}</td>
                    <td className="px-4 py-2.5">
                      <button
                        onClick={() => navigate(`/tasks/${t.id}`)}
                        className="font-medium text-[#111216] hover:text-[#4353FF] transition-colors flex items-center gap-1"
                        data-testid={`archive-open-${t.id}`}
                      >
                        {t.title}
                        <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100" />
                      </button>
                    </td>
                    <td className="px-4 py-2.5 text-[#686B74]">{t.creator_name}</td>
                    <td className="px-4 py-2.5 text-[#686B74]">{t.owner_name}</td>
                    <td className="px-4 py-2.5">
                      <Badge variant="outline" className={`text-[11px] font-medium ${PRIORITY_COLORS[t.priority] || ""}`}>
                        {t.priority}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-[#686B74]">
                      {t.updated_at ? format(new Date(t.updated_at), "MMM d, yyyy") : "-"}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        onClick={() => setDeleteConfirm(t)}
                        className="p-1.5 text-[#686B74] hover:text-[#E24A4A] transition-colors"
                        data-testid={`archive-delete-${t.id}`}
                        title="Delete permanently"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center">
                      <Archive className="w-8 h-8 text-[#EBEBEB] mx-auto mb-2" />
                      <p className="text-sm text-[#686B74]">
                        {search ? "No archived tasks match your search" : "No archived tasks"}
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent data-testid="archive-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#E24A4A]">Delete Archived Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#111216]">
              Are you sure you want to permanently delete <span className="font-semibold">{deleteConfirm?.title}</span>?
            </p>
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
              This will permanently remove the task, its messages, attachments, and activity history. This cannot be undone.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button
              data-testid="confirm-archive-delete-btn"
              onClick={() => handleDelete(deleteConfirm?.id)}
              className="bg-[#E24A4A] hover:bg-red-600 text-white"
            >
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
